import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { Type, List, MousePointer2, FormInput, Languages, Save } from "lucide-react";
import { DEFAULT_LOCALE, TOAST_LOAD_ERROR, TOAST_NO_PERMISSION, TOAST_UPDATED, TOAST_SAVE_ERROR, PLH_LOGO, PLH_SUBTITLE, PLH_CTA_LABEL } from "../../lib/constants";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import AdminHeadersEditor from "./AdminHeadersEditor";
import AdminListsEditor from "./AdminListsEditor";
import AdminInterfaceEditor from "./AdminInterfaceEditor";
import AdminFormConfig from "./AdminFormConfig";
import {
  getSectionHeaders, updateSectionHeader,
  getNavigation, updateNavigation,
  getFormServices, updateFormServices,
  getContactMethods, updateContactMethods,
  getUiStrings, updateUiStrings,
  getFormPrefixes, updateFormPrefixes,
  getFormNationalities, updateFormNationalities,
  getLocales,
} from "../../lib/api";
import { logger } from "../../lib/logger";
import { UI_CONFIG_TABS as UI_TABS, UI_FORM_CHILDREN as FORM_CHILDREN } from "../../lib/tabs";

const _tabDataLoaded = {};

export default function AdminUiConfig({ readOnly = false, subTabModes = {}, initialTab, embedded, formInitialTab, activeLang: propLang, onActiveLangChange: setPropLang, sectionHeaders: propSectionHeaders, setSectionHeaders: propSetSectionHeaders, handleSaveSectionHeaders: propHandleSaveSectionHeaders, saving: parentSaving, availableLangs: propLangs }) {
  const [activeTab, setActiveTab] = useState(initialTab || "headers");
  const prevTab = useRef(initialTab);
  useEffect(() => { if (initialTab && initialTab !== prevTab.current) { setActiveTab(initialTab); delete _tabDataLoaded[prevTab.current]; prevTab.current = initialTab; } }, [initialTab]);
  const [formSubTab, setFormSubTab] = useState(formInitialTab || "labels");
  useEffect(() => { if (formInitialTab && formInitialTab !== formSubTab) setFormSubTab(formInitialTab); }, [formInitialTab]);
  useEffect(() => { delete _tabDataLoaded.form; }, [formSubTab]);
  const [availableLangs, setAvailableLangs] = useState(propLangs || [{ code: DEFAULT_LOCALE, label: "PT", name: "Português" }]);
  const [localActiveLang, setLocalActiveLang] = useState(DEFAULT_LOCALE);
  const activeLang = propLang || localActiveLang;
  const setActiveLang = setPropLang || setLocalActiveLang;
  const effectiveReadOnly = readOnly;
  const [sectionHeaders, setSectionHeaders] = useState(null);
  const [navigation, setNavigation] = useState(null);
  const [formServices, setFormServices] = useState(null);
  const [contactMethods, setContactMethods] = useState(null);
  const [uiStrings, setUiStrings] = useState(null);
  const [prefixes, setPrefixes] = useState([]);
  const [nationalities, setNationalities] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const _locLoaded = useRef(false);
  useEffect(() => {
    if (_locLoaded.current || propLangs) return;
    _locLoaded.current = true;
    getLocales().then(data => {
      if (!Array.isArray(data)) return;
      const active = data.filter(l => l.active === 1 && !l.deleted_at);
      if (active.length > 0) {
        const mapped = active.map(l => ({ code: l.code, label: l.label?.substring(0, 2).toUpperCase() || l.code, name: l.label, flag: l.flag || "" }));
        setAvailableLangs(mapped);
        if (!mapped.find(l => l.code === activeLang)) setActiveLang(mapped[0].code);
      }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (activeTab === "form" && formSubTab === "prefixes") return;
    Object.keys(_tabDataLoaded).forEach(k => delete _tabDataLoaded[k]);
    setSectionHeaders(null); setNavigation(null); setFormServices(null);
    setContactMethods(null); setUiStrings(null); setPrefixes([]); setNationalities([]);
  }, [activeLang, activeTab, formSubTab]);

  useEffect(() => {
    if (_tabDataLoaded[activeTab]) return;
    _tabDataLoaded[activeTab] = true;
    setLoading(true);
    const loaders = {
      headers: () => getSectionHeaders(activeLang).then(d => setSectionHeaders(d || {})),
      lists: () => getNavigation(activeLang).then(d => setNavigation(d || [])),
      interface: () => getUiStrings(activeLang).then(d => setUiStrings(d || {})),
      texts: () => getUiStrings(activeLang).then(d => setUiStrings(d || {})),
      form: () => {
        console.log("[AdminUiConfig] form loader running for formSubTab:", formSubTab);
        const needs = new Set();
        if (formSubTab === "services" || formSubTab === "contacts" || formSubTab === "prefixes" || formSubTab === "nationalities" || formSubTab === "labels" || formSubTab === "texts") {
          if (formSubTab === "services") { needs.add("services"); }
          if (formSubTab === "contacts") { needs.add("contacts"); }
          if (formSubTab === "prefixes") { needs.add("prefixes"); }
          if (formSubTab === "nationalities") { needs.add("nationalities"); }
          if (formSubTab === "labels") { needs.add("ui"); }
          if (formSubTab === "texts") { needs.add("ui"); }
        } else {
          needs.add("services"); needs.add("contacts"); needs.add("prefixes"); needs.add("nationalities"); needs.add("ui");
        }
        if (!needs.has("ui")) needs.add("ui");
        const ps = [];
        if (needs.has("services")) ps.push(getFormServices(activeLang).then(d => setFormServices(d || [])));
        if (needs.has("contacts")) ps.push(getContactMethods(activeLang).then(d => setContactMethods(d || [])));
        if (needs.has("prefixes")) ps.push(getFormPrefixes().then(d => setPrefixes(d || [])));
        if (needs.has("nationalities")) ps.push(getFormNationalities(activeLang).then(d => setNationalities(d || [])));
        if (needs.has("ui")) ps.push(getUiStrings(activeLang).then(d => setUiStrings(d || {})));
        return Promise.all(ps);
      },
    };
    (loaders[activeTab]?.() || Promise.resolve()).catch(err => {
      logger.error(err); toast.error("Erro ao carregar dados.");
    }).finally(() => setLoading(false));
  }, [activeTab, activeLang, formSubTab]);

  async function handleSaveSectionHeaders() {
    if (effectiveReadOnly) { toast.warning("Sem permissão para editar."); return; }
    setSaving(true);
    try {
      for (const [section, content] of Object.entries(sectionHeaders)) {
        await updateSectionHeader(section, content, activeLang);
      }
      toast.success(`Títulos atualizados (${activeLang}).`);
    } catch (err) {
      logger.error(err);
      toast.error("Erro ao salvar títulos.");
    } finally {
      setSaving(false);
    }
  }

  async function handleSaveNavigation() {
    if (effectiveReadOnly) { toast.warning("Sem permissão para editar."); return; }
    setSaving(true);
    try { 
      await updateNavigation(navigation, activeLang); 
      toast.success(`Menu de navegação atualizado (${activeLang}).`); 
    }
    catch (err) { logger.error(err); toast.error("Erro ao salvar navegação."); }
    finally { setSaving(false); }
  }

  async function handleSaveFormServices(items) {
    if (effectiveReadOnly) { toast.warning("Sem permissão para editar."); return; }
    setSaving(true);
    try { 
      await updateFormServices(items, activeLang); 
      setFormServices(items); 
    }
    catch (err) { logger.error(err); toast.error("Erro ao salvar opções do formulário."); }
    finally { setSaving(false); }
  }

  async function handleSaveContactMethods(items) {
    if (effectiveReadOnly) { toast.warning("Sem permissão para editar."); return; }
    setSaving(true);
    try { 
      await updateContactMethods(items, activeLang); 
      setContactMethods(items); 
    }
    catch (err) { logger.error(err); toast.error("Erro ao salvar métodos de contato."); }
    finally { setSaving(false); }
  }

  async function handleSavePrefixes(items) {
    if (effectiveReadOnly) { toast.warning("Sem permissão para editar."); return; }
    setSaving(true);
    try { 
      await updateFormPrefixes(items); 
      setPrefixes(items); 
      toast.success("Prefixos atualizados."); 
    }
    catch (err) { logger.error(err); toast.error("Erro ao salvar prefixos."); }
    finally { setSaving(false); }
  }

  async function handleSaveNationalities(names) {
    if (effectiveReadOnly) { toast.warning("Sem permissão para editar."); return; }
    setSaving(true);
    try { 
      await updateFormNationalities(names, activeLang); 
      setNationalities(names); 
      toast.success(`Nacionalidades atualizadas (${activeLang}).`); 
    }
    catch (err) { logger.error(err); toast.error("Erro ao salvar nacionalidades."); }
    finally { setSaving(false); }
  }

  async function handleSaveUiStrings(contextFilter) {
    if (effectiveReadOnly) { toast.warning("Sem permissão para editar."); return; }
    setSaving(true);
    try {
      const entries = contextFilter
        ? [[contextFilter, uiStrings[contextFilter]]].filter(([_, v]) => v)
        : Object.entries(uiStrings);
      for (const [context, values] of entries) { 
        await updateUiStrings(context, values, activeLang); 
      }
      toast.success(`Textos de interface atualizados (${activeLang}).`);
    } catch (err) { logger.error(err); toast.error("Erro ao salvar textos de interface."); }
    finally { setSaving(false); }
  }

  function isReadOnly(sub) { return effectiveReadOnly || subTabModes[`ui_${sub}`] === "readonly"; }

  const activeLangName = availableLangs.find(l => l.code === activeLang)?.name || activeLang;

  if (embedded) {
    return (
      <div className="relative">
        {loading && (
          <div className="absolute inset-0 bg-white/60 backdrop-blur-[1px] z-10 flex items-center justify-center">
            <div className="flex flex-col items-center gap-3"><div className="w-6 h-6 border-2 border-champagne border-t-transparent rounded-full animate-spin" /><span className="text-[10px] uppercase tracking-[0.2em] text-warmgray animate-pulse">Carregando {activeLangName}...</span></div>
          </div>
        )}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <div className="md:hidden overflow-x-auto no-scrollbar mb-4">
            <TabsList className="w-max min-w-full border-b border-fendi p-0 h-auto justify-start flex-nowrap rounded-none bg-transparent">
              {UI_TABS.filter(t => t.value === activeTab).map(({ value, icon: Icon, label }) => (
                <TabsTrigger key={value} value={value} className="relative shrink-0 px-3 py-3 data-[state=active]:text-softblack data-[state=active]:after:opacity-100 after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-champagne after:opacity-0 transition-all rounded-none text-warmgray">
                  <Icon size={16} />
                </TabsTrigger>
              ))}
            </TabsList>
          </div>
          <TabsContent value="headers" className="mt-0"><AdminHeadersEditor readOnly={isReadOnly("headers")} sectionHeaders={sectionHeaders} setSectionHeaders={setSectionHeaders} saving={saving} handleSaveSectionHeaders={handleSaveSectionHeaders} /></TabsContent>
<TabsContent value="lists" className="mt-0"><AdminListsEditor readOnly={isReadOnly("lists")} navigation={navigation} setNavigation={setNavigation} saving={saving} handleSaveNavigation={handleSaveNavigation} activeLang={activeLang} /></TabsContent>
            <TabsContent value="texts" className="mt-0">
            <div className="bg-white border border-fendi/40 rounded-xl overflow-hidden">
              <div className="px-6 py-4 border-b border-fendi/10 bg-fendi/5 flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-full bg-champagne/10 flex items-center justify-center text-champagne"><Type size={14} /></div>
                  <span className="text-xs font-medium text-softblack uppercase tracking-wider">Textos do Menu</span>
                </div>
                {!effectiveReadOnly && (
                  <button onClick={() => updateUiStrings("general", { logo: uiStrings?.general?.logo || "", cta_label: uiStrings?.general?.cta_label || "", subtitle: uiStrings?.general?.subtitle || "" }, activeLang).then(() => toast.success("Textos atualizados")).catch(() => toast.error("Erro ao salvar"))} className="btn-primary !py-1.5 !px-3 !text-[9px]">
                    <Save size={11} className="mr-1.5" /> Guardar
                  </button>
                )}
              </div>
              <div className="p-6 grid sm:grid-cols-3 gap-6">
                <div>
                  <span className="text-[9px] uppercase tracking-wider text-warmgray font-medium">Logo (Texto)</span>
                  <input className="w-full text-xs px-3 py-2 mt-1.5 border border-fendi/20 rounded-lg bg-transparent focus:border-champagne focus:ring-1 focus:ring-champagne/20 outline-none transition-all" value={uiStrings?.general?.logo || ""} onChange={e => setUiStrings(p => ({ ...p, general: { ...p?.general, logo: e.target.value } }))} placeholder="ex: Hub" />
                </div>
                <div>
                  <span className="text-[9px] uppercase tracking-wider text-warmgray font-medium">Slogan / Subtítulo</span>
                  <input className="w-full text-xs px-3 py-2 mt-1.5 border border-fendi/20 rounded-lg bg-transparent focus:border-champagne focus:ring-1 focus:ring-champagne/20 outline-none transition-all" value={uiStrings?.general?.subtitle || ""} onChange={e => setUiStrings(p => ({ ...p, general: { ...p?.general, subtitle: e.target.value } }))} placeholder="ex: Consultoria · Património · Design" />
                </div>
                <div>
                  <span className="text-[9px] uppercase tracking-wider text-warmgray font-medium">Botão: CTA Global (Header)</span>
                  <input className="w-full text-xs px-3 py-2 mt-1.5 border border-fendi/20 rounded-lg bg-transparent focus:border-champagne focus:ring-1 focus:ring-champagne/20 outline-none transition-all" value={uiStrings?.general?.cta_label || ""} onChange={e => setUiStrings(p => ({ ...p, general: { ...p?.general, cta_label: e.target.value } }))} placeholder="ex: Agendar Consultoria" />
                </div>
              </div>
            </div>
          </TabsContent>
          <TabsContent value="interface" className="mt-0"><AdminInterfaceEditor readOnly={isReadOnly("interface")} uiStrings={uiStrings} setUiStrings={setUiStrings} saving={saving} handleSaveUiStrings={handleSaveUiStrings} /></TabsContent>
          <TabsContent value="form" className="mt-0"><AdminFormConfig initialTab={formInitialTab} embedded readOnly={isReadOnly("form")} subTabModes={subTabModes} uiStrings={uiStrings} setUiStrings={setUiStrings} saving={saving} handleSaveUiStrings={handleSaveUiStrings} formServices={formServices} setFormServices={setFormServices} handleSaveFormServices={handleSaveFormServices} contactMethods={contactMethods} setContactMethods={setContactMethods} handleSaveContactMethods={handleSaveContactMethods} prefixes={prefixes} setPrefixes={setPrefixes} handleSavePrefixes={handleSavePrefixes} nationalities={nationalities} setNationalities={setNationalities} handleSaveNationalities={handleSaveNationalities} sectionHeaders={propSectionHeaders} setSectionHeaders={propSetSectionHeaders} handleSaveSectionHeaders={propHandleSaveSectionHeaders} parentSaving={parentSaving} activeLang={activeLang} /></TabsContent>
        </Tabs>
      </div>
    );
  }

  return (
    <div data-id="admin-ui-config" className="flex flex-col gap-6 w-full">
      <div className="bg-white border border-fendi/40 rounded-xl p-4 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-champagne/10 flex items-center justify-center text-champagne">
            <Languages size={20} />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-softblack uppercase tracking-wider">Idioma de Edição</h3>
            <p className="text-[11px] text-warmgray">Editando conteúdo em: <span className="text-softblack font-medium">{activeLangName}</span></p>
          </div>
        </div>
        <div className="flex items-center gap-2 p-1 bg-fendi/5 rounded-lg border border-fendi/20">
          {availableLangs.map((lang) => (
            <button key={lang.code} onClick={() => setActiveLang(lang.code)}
              className={`px-4 py-1.5 rounded-md text-[11px] font-bold tracking-widest transition-all ${activeLang === lang.code ? "bg-white text-softblack shadow-sm ring-1 ring-fendi/20" : "text-warmgray hover:text-softblack hover:bg-white/50"}`}
            >{lang.flag || lang.label}</button>
          ))}
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <div className="md:hidden overflow-x-auto no-scrollbar mb-4">
          <TabsList className="w-max min-w-full border-b border-fendi p-0 h-auto justify-start flex-nowrap rounded-none bg-transparent">
            {UI_TABS.map(({ value, icon: Icon, label }) => (
              <TabsTrigger key={value} value={value} className="relative shrink-0 px-3 py-3 data-[state=active]:text-softblack data-[state=active]:after:opacity-100 after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-champagne after:opacity-0 transition-all rounded-none text-warmgray hover:text-softblack">
                <Icon size={16} />
              </TabsTrigger>
            ))}
          </TabsList>
        </div>

        <div className="flex gap-0 bg-white border border-fendi/40 rounded-xl shadow-sm min-h-[500px]">
          <TabsList className="hidden md:flex flex-col w-36 shrink-0 border-r border-fendi/40 p-0 h-auto justify-start rounded-none bg-fendi/5">
            {UI_TABS.map(({ value, icon: Icon, label }) =>
              value === "form" ? (
                <div key={value} className="w-full">
                  <TabsTrigger value={value} className="relative w-full px-4 py-3 data-[state=active]:bg-white data-[state=active]:text-softblack data-[state=active]:after:opacity-100 after:absolute after:right-0 after:top-2 after:bottom-2 after:w-0.5 after:bg-champagne after:opacity-0 transition-all rounded-none justify-start flex items-center gap-3 text-warmgray hover:text-softblack hover:bg-white/50">
                    <Icon size={15} />
                    <span className="flex-1 text-left text-[11px] uppercase tracking-wider">{label}</span>
                  </TabsTrigger>
                  {activeTab === "form" && (
                    <div className="border-t border-fendi/20 bg-fendi/5">
                      {FORM_CHILDREN.map((c) => (
                        <button
                          key={c.value}
                          onClick={() => setFormSubTab(c.value)}
                          className={`w-full text-left px-4 py-2 pl-10 text-[10px] tracking-wider transition-colors ${formSubTab === c.value ? "text-champagne font-bold bg-white" : "text-warmgray hover:text-softblack hover:bg-white/50"}`}
                        >
                          {c.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <TabsTrigger key={value} value={value} className="relative w-full px-4 py-3 data-[state=active]:bg-white data-[state=active]:text-softblack data-[state=active]:after:opacity-100 after:absolute after:right-0 after:top-2 after:bottom-2 after:w-0.5 after:bg-champagne after:opacity-0 transition-all rounded-none justify-start flex items-center gap-3 text-warmgray hover:text-softblack hover:bg-white/50">
                  <Icon size={15} />
                  <span className="text-[11px] uppercase tracking-wider">{label}</span>
                </TabsTrigger>
              )
            )}
          </TabsList>

          <div className="flex-1 p-4 md:p-8 relative">
            {loading && (
              <div className="absolute inset-0 bg-white/60 backdrop-blur-[1px] z-10 flex items-center justify-center">
                <div className="flex flex-col items-center gap-3"><div className="w-6 h-6 border-2 border-champagne border-t-transparent rounded-full animate-spin" /><span className="text-[10px] uppercase tracking-[0.2em] text-warmgray animate-pulse">Carregando {activeLangName}...</span></div>
              </div>
            )}
            <TabsContent value="headers" className="mt-0"><AdminHeadersEditor readOnly={isReadOnly("headers")} sectionHeaders={sectionHeaders} setSectionHeaders={setSectionHeaders} saving={saving} handleSaveSectionHeaders={handleSaveSectionHeaders} /></TabsContent>
          <TabsContent value="lists" className="mt-0"><AdminListsEditor readOnly={isReadOnly("lists")} navigation={navigation} setNavigation={setNavigation} saving={saving} handleSaveNavigation={handleSaveNavigation} activeLang={activeLang} /></TabsContent>

  
            <TabsContent value="texts" className="mt-0">
              <div className="bg-white border border-fendi/40 rounded-xl overflow-hidden">
                <div className="px-6 py-4 border-b border-fendi/10 bg-fendi/5 flex items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-full bg-champagne/10 flex items-center justify-center text-champagne"><Type size={14} /></div>
                    <span className="text-xs font-medium text-softblack uppercase tracking-wider">Textos do Menu</span>
                  </div>
                  {!effectiveReadOnly && (
                    <button onClick={() => updateUiStrings("general", { logo: uiStrings?.general?.logo || "", cta_label: uiStrings?.general?.cta_label || "", subtitle: uiStrings?.general?.subtitle || "" }, activeLang).then(() => toast.success("Textos atualizados")).catch(() => toast.error("Erro ao salvar"))} className="btn-primary !py-1.5 !px-3 !text-[9px]">
                      <Save size={11} className="mr-1.5" /> Guardar
                    </button>
                  )}
                </div>
                  <div className="p-6 grid sm:grid-cols-3 gap-6">
                    <div>
                      <span className="text-[9px] uppercase tracking-wider text-warmgray font-medium">Logo (Texto)</span>
                      <input className="w-full text-xs px-3 py-2 mt-1.5 border border-fendi/20 rounded-lg bg-transparent focus:border-champagne focus:ring-1 focus:ring-champagne/20 outline-none transition-all" value={uiStrings?.general?.logo || ""} onChange={e => setUiStrings(p => ({ ...p, general: { ...p?.general, logo: e.target.value } }))} placeholder="ex: Hub" />
                    </div>
                    <div>
                      <span className="text-[9px] uppercase tracking-wider text-warmgray font-medium">Slogan / Subtítulo</span>
                      <input className="w-full text-xs px-3 py-2 mt-1.5 border border-fendi/20 rounded-lg bg-transparent focus:border-champagne focus:ring-1 focus:ring-champagne/20 outline-none transition-all" value={uiStrings?.general?.subtitle || ""} onChange={e => setUiStrings(p => ({ ...p, general: { ...p?.general, subtitle: e.target.value } }))} placeholder="ex: Consultoria · Património · Design" />
                    </div>
                    <div>
                      <span className="text-[9px] uppercase tracking-wider text-warmgray font-medium">Botão: CTA Global (Header)</span>
                      <input className="w-full text-xs px-3 py-2 mt-1.5 border border-fendi/20 rounded-lg bg-transparent focus:border-champagne focus:ring-1 focus:ring-champagne/20 outline-none transition-all" value={uiStrings?.general?.cta_label || ""} onChange={e => setUiStrings(p => ({ ...p, general: { ...p?.general, cta_label: e.target.value } }))} placeholder="ex: Agendar Consultoria" />
                    </div>
                  </div>
                </div>
              </TabsContent>
              <TabsContent value="interface" className="mt-0"><AdminInterfaceEditor readOnly={isReadOnly("interface")} uiStrings={uiStrings} setUiStrings={setUiStrings} saving={saving} handleSaveUiStrings={handleSaveUiStrings} /></TabsContent>
            <TabsContent value="form" className="mt-0"><AdminFormConfig initialTab={formSubTab} embedded readOnly={isReadOnly("form")} subTabModes={subTabModes} uiStrings={uiStrings} setUiStrings={setUiStrings} saving={saving} handleSaveUiStrings={handleSaveUiStrings} formServices={formServices} setFormServices={setFormServices} handleSaveFormServices={handleSaveFormServices} contactMethods={contactMethods} setContactMethods={setContactMethods} handleSaveContactMethods={handleSaveContactMethods} prefixes={prefixes} setPrefixes={setPrefixes} handleSavePrefixes={handleSavePrefixes} nationalities={nationalities} setNationalities={setNationalities} handleSaveNationalities={handleSaveNationalities} sectionHeaders={propSectionHeaders} setSectionHeaders={propSetSectionHeaders} handleSaveSectionHeaders={propHandleSaveSectionHeaders} parentSaving={parentSaving} activeLang={activeLang} /></TabsContent>
          </div>
        </div>
      </Tabs>
    </div>
  );
}
